// Bulk asignar responsable por pañol — /bodega/panoles/bulk-responsables
// Tabla única con todos los pañoles: autocomplete de usuario → rellena rut + tipo.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Users, Save, Loader2, ArrowLeft, Search, CheckCircle2, Package,
} from 'lucide-react';
import { toast } from 'sonner';

const TIPOS = ['', 'supervisor', 'panolero', 'chofer', 'jefe_faena'];

export default function PanolesBulkResponsablePage() {
  const navigate = useNavigate();
  const [panoles, setPanoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [changes, setChanges] = useState({}); // panol_id → {responsable_nombre, rut, tipo}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const inputRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        api.get('/bodega/panoles?activos_solo=true'),
        api.get('/admin/users').catch(() => ({ data: { items: [] } })),
      ]);
      setPanoles(p.data.items || []);
      setUsers((u.data.items || u.data.users || []).filter((x) => x.activo !== false));
    } catch (err) {
      toast.error('Error cargando datos');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const panolesFiltrados = panoles.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.nombre || '').toLowerCase().includes(q)
      || (p.responsable_nombre || '').toLowerCase().includes(q);
  });

  const getCurrentValue = (panol, field) => {
    const ch = changes[panol.id];
    if (ch && field in ch) return ch[field];
    const map = {
      nombre: panol.responsable_nombre || '',
      rut: panol.responsable_rut || '',
      tipo: panol.responsable_tipo || '',
    };
    return map[field];
  };

  const setChange = (panolId, field, value) => {
    setChanges((prev) => ({
      ...prev,
      [panolId]: { ...(prev[panolId] || {}), [field]: value },
    }));
  };

  const autoCompleteFromUser = (panolId, nombre) => {
    const u = users.find((x) =>
      (x.nombre || x.name || '').toLowerCase() === nombre.toLowerCase(),
    );
    if (u) {
      setChanges((prev) => ({
        ...prev,
        [panolId]: {
          ...(prev[panolId] || {}),
          nombre: u.nombre || u.name,
          rut: u.rut || '',
        },
      }));
    }
  };

  const saveAll = async () => {
    const items = Object.entries(changes).map(([panol_id, vals]) => ({
      panol_id,
      responsable_nombre: vals.nombre !== undefined
        ? vals.nombre
        : (panoles.find((p) => p.id === panol_id)?.responsable_nombre || ''),
      responsable_rut: vals.rut !== undefined
        ? vals.rut
        : (panoles.find((p) => p.id === panol_id)?.responsable_rut || ''),
      responsable_tipo: vals.tipo !== undefined
        ? vals.tipo
        : (panoles.find((p) => p.id === panol_id)?.responsable_tipo || ''),
    }));
    if (items.length === 0) { toast.info('Sin cambios pendientes'); return; }
    setSaving(true);
    try {
      const res = await api.put('/bodega/panoles/bulk-responsables', { items });
      toast.success(`${res.data.actualizados} pañoles actualizados`);
      setChanges({});
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally { setSaving(false); }
  };

  const changesCount = Object.keys(changes).length;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="panoles-bulk-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate('/bodega/panoles')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-600" /> Asignar responsables por pañol
          </h1>
          <p className="text-sm text-slate-500">
            Tabla unificada para asignar/cambiar el responsable de cada pañol. Los emails se resuelven por RUT.
          </p>
        </div>
        <Button
          onClick={saveAll} disabled={saving || changesCount === 0}
          data-testid="btn-guardar-todos"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Guardar {changesCount > 0 && `(${changesCount})`}
        </Button>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              placeholder="Filtrar por nombre de pañol o responsable..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="text-sm"
              data-testid="input-search"
            />
            <Badge variant="outline" className="text-xs">
              {panolesFiltrados.length} / {panoles.length}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline" /> Cargando...
            </div>
          ) : panolesFiltrados.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-2 opacity-40" />
              Sin pañoles. Ejecuta primero la migración Access para crearlos.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 w-5"></th>
                    <th className="text-left px-3 py-2">Pañol</th>
                    <th className="text-left px-3 py-2">Responsable (nombre)</th>
                    <th className="text-left px-3 py-2 w-32">RUT</th>
                    <th className="text-left px-3 py-2 w-36">Tipo</th>
                    <th className="text-right px-3 py-2 w-16">Htas</th>
                  </tr>
                </thead>
                <tbody>
                  {panolesFiltrados.map((p) => {
                    const ch = changes[p.id];
                    const edited = !!ch;
                    return (
                      <tr
                        key={p.id}
                        className={`border-b ${edited ? 'bg-indigo-50' : ''}`}
                        data-testid={`row-panol-${p.id}`}
                      >
                        <td className="px-3 py-1.5">
                          {edited && <CheckCircle2 className="w-3 h-3 text-indigo-600" />}
                        </td>
                        <td className="px-3 py-1.5 font-semibold">{p.nombre}</td>
                        <td className="px-3 py-1.5">
                          <Input
                            ref={(el) => (inputRefs.current[p.id] = el)}
                            list={`users-list-${p.id}`}
                            value={getCurrentValue(p, 'nombre')}
                            onChange={(e) => setChange(p.id, 'nombre', e.target.value)}
                            onBlur={(e) => autoCompleteFromUser(p.id, e.target.value)}
                            className="text-xs h-8"
                            placeholder="Nombre completo..."
                            data-testid={`input-nombre-${p.id}`}
                          />
                          <datalist id={`users-list-${p.id}`}>
                            {users.map((u) => (
                              <option key={u.id || u.email} value={u.nombre || u.name}>
                                {u.rut} · {u.email}
                              </option>
                            ))}
                          </datalist>
                        </td>
                        <td className="px-3 py-1.5">
                          <Input
                            value={getCurrentValue(p, 'rut')}
                            onChange={(e) => setChange(p.id, 'rut', e.target.value)}
                            className="text-xs h-8 font-mono"
                            placeholder="12.345.678-9"
                            data-testid={`input-rut-${p.id}`}
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <Select
                            value={getCurrentValue(p, 'tipo') || '__none__'}
                            onValueChange={(v) => setChange(p.id, 'tipo', v === '__none__' ? '' : v)}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid={`select-tipo-${p.id}`}>
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">—</SelectItem>
                              {TIPOS.filter(Boolean).map((t) => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs text-slate-500">
                          {p.total_herramientas || 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {changesCount > 0 && (
        <div className="sticky bottom-4 flex justify-center">
          <Button
            size="lg" onClick={saveAll} disabled={saving}
            className="shadow-lg"
            data-testid="btn-guardar-floating"
          >
            {saving ? <Loader2 className="w-5 h-5 mr-1 animate-spin" /> : <Save className="w-5 h-5 mr-1" />}
            Guardar {changesCount} cambio{changesCount > 1 ? 's' : ''}
          </Button>
        </div>
      )}
    </div>
  );
}
